import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowRight, Check, ChevronDown, ChevronUp, Lock, Plus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient, { ApiClientError } from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import type { BusinessDay, CashDrawerMovement } from '@/types'
import { netCashAdjustments } from './dayCloseHelpers'
import { TenderCountStep } from './TenderCountStep'

/** Fallback in PKR. Mirrors backend dayops.DefaultVarianceThreshold. */
const DEFAULT_VARIANCE_THRESHOLD = 100

interface CloseDayWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  day: BusinessDay
  cashSales: number
  cardSales: number
  onlineSales: number
  /** Real open orders still holding tables/revenue (F-CTRL-N25). A non-zero
   *  count requires a closing note before the day can lock. */
  unresolvedOpenOrders: number
  movements: CashDrawerMovement[] | undefined
  /**
   * True while the parent's cash-sales / movements queries are loading for
   * the first time. Until they settle, `cashSales` and `movements` default
   * to 0 / [], which would make the wizard's variance computation lie:
   * a staged day with a real Rs 5,000 shortfall would briefly render as
   * "drawer matches!" because expected_cash collapses to opening_cash.
   * The review step suppresses its banner + locks Finish until this is false.
   */
  isLoadingSummary: boolean
}

type Step = 'count-cash' | 'count-card' | 'count-online' | 'review'

const STEP_ORDER: Step[] = ['count-cash', 'count-card', 'count-online', 'review']

/**
 * End-of-day wizard, redesigned for cashiers. Internally still uses the
 * two-phase stage/lock backend pattern so we can show a clear retry path
 * if the lock call fails after staging succeeds — but the user never sees
 * the word "staged".
 *
 *   Step 1: count the cash in the drawer
 *   Step 2: card machine total
 *   Step 3: online & wallet total
 *   Step 4: review the differences and finish
 *
 * On Finish: stage-close → lock-close, fired back-to-back. If lock fails,
 * the dialog stays open on the review step with a clear retry button.
 *
 * F-CTRL-N33 reshaped steps 2–3. They used to be a single optional screen —
 * two bare inputs, a Skip button, and no keypad, so on a touchscreen till the
 * card and online totals could not be entered at all. Skipping stored NULL,
 * computed no variance, and left the review screen silent about both tenders,
 * so a card shortfall was invisible to everyone. Now each tender gets its own
 * step with its own keypad and cannot be passed without a number. Splitting
 * them (rather than putting one keypad above two fields) is deliberate: a
 * shared keypad needs an active-field concept, and typing the card total into
 * the online box would reconcile the day to a lie.
 */
export function CloseDayWizard({
  open,
  onOpenChange,
  day,
  cashSales,
  cardSales,
  onlineSales,
  unresolvedOpenOrders,
  movements,
  isLoadingSummary,
}: CloseDayWizardProps) {
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()

  const adj = useMemo(() => netCashAdjustments(movements), [movements])
  const paidIn = useMemo(
    () =>
      (movements ?? [])
        .filter((m) => m.movement_type === 'paid_in')
        .reduce((sum, m) => sum + m.amount, 0),
    [movements],
  )
  const paidOut = useMemo(
    () =>
      (movements ?? [])
        .filter((m) => m.movement_type === 'paid_out')
        .reduce((sum, m) => sum + m.amount, 0),
    [movements],
  )
  const expectedCash = (day.opening_cash ?? 0) + cashSales + adj

  const [step, setStep] = useState<Step>('count-cash')
  const [countedCash, setCountedCash] = useState<string>('')
  const [countedCard, setCountedCard] = useState<string>('')
  const [countedOnline, setCountedOnline] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [showMath, setShowMath] = useState(false)
  const [showNotes, setShowNotes] = useState(false)
  /** Surface a friendly retry banner if lock failed after stage succeeded. */
  const [lockFailed, setLockFailed] = useState(false)

  // Reset wizard state only when the dialog opens — NOT every time the parent
  // refetches `day`. The previous dep list included day.status / day.counted_*
  // which caused a refetch mid-edit to wipe the user's typed values and (worse)
  // clobber `lockFailed=true` back to false the instant the staged-row refetch
  // landed, so the "Almost done — tap Finish to lock" banner would flicker.
  useEffect(() => {
    if (open) {
      const isStaged = day.status === 'staged'
      const cash = isStaged && day.counted_cash != null ? String(day.counted_cash) : ''
      const card = isStaged && day.counted_card != null ? String(day.counted_card) : ''
      const online = isStaged && day.counted_online != null ? String(day.counted_online) : ''
      setCountedCash(cash)
      setCountedCard(card)
      setCountedOnline(online)
      // Jump to review only when the staged row actually carries all three
      // counts. A day staged by an older build has NULL card/online: landing
      // on review would paint steps 2–3 green (StepIndicator colours every
      // step below the current one) for screens the operator never saw, and
      // the backend would then refuse the lock with tender_count_required.
      // Walk the wizard instead, pre-filled with whatever is known.
      const complete = cash !== '' && card !== '' && online !== ''
      setStep(isStaged && complete ? 'review' : 'count-cash')
      setNotes(isStaged && day.closing_notes ? day.closing_notes : '')
      setShowMath(false)
      setShowNotes(false)
      setLockFailed(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const parsedCash = Number(countedCash) || 0
  const parsedCard = Number(countedCard) || 0
  const parsedOnline = Number(countedOnline) || 0
  const cashVariance = parsedCash - expectedCash
  // A blank field is NOT "Rs 0 matches" — it is "nobody counted this". Each
  // step's Next stays disabled until a real number is typed, so by the review
  // step all three of these are true (F-CTRL-N33).
  const cardEntered = countedCard.trim() !== '' && Number.isFinite(Number(countedCard))
  const onlineEntered = countedOnline.trim() !== '' && Number.isFinite(Number(countedOnline))
  const isCountValid = countedCash.trim() !== '' && Number.isFinite(Number(countedCash))

  const thresholdQuery = useQuery({
    queryKey: ['settings', 'day_close_variance_threshold'],
    queryFn: () => apiClient.getSetting('day_close_variance_threshold'),
    staleTime: 5 * 60_000,
  })
  const varianceThreshold = useMemo(() => {
    const raw = thresholdQuery.data?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : DEFAULT_VARIANCE_THRESHOLD
  }, [thresholdQuery.data])

  // Cross-day tender bridge: card/online swiped TODAY for orders from earlier
  // days. On today's machine/bank batch but booked to those days, so it explains
  // a card/online figure that reads higher than "our records" (the Rasoi case).
  const tenderReconQuery = useQuery({
    queryKey: ['day-ops', 'tender-recon', day.id],
    queryFn: () => apiClient.getTenderReconciliation(day.id),
    enabled: open,
    staleTime: 60_000,
  })
  const tenderRecon = tenderReconQuery.data?.success ? tenderReconQuery.data.data : null
  const hasCrossDay = !!tenderRecon && (tenderRecon.card_cross_day > 0 || tenderRecon.online_cross_day > 0)
  const absVariance = Math.abs(cashVariance)
  const isMatch = absVariance < 0.01
  const isSmallOff = !isMatch && absVariance <= varianceThreshold
  const isBigOff = absVariance > varianceThreshold

  // Card/online reconcile against a POINT, not a band: the machine/bank batch is
  // expected to total sales + cross-day exactly. Cross-day payments (swiped today
  // for earlier orders, whose sale books on those days — see the note above) were
  // physically swiped today, so they land on today's batch for certain. Anything
  // short of the expected point is money missing from the batch; anything over is
  // money on the batch we can't account for. Both, like cash, need a reason above
  // the threshold. (A band [sales, sales+crossDay] used to let a large crossDay
  // swallow a genuine shortfall — e.g. Rs 50,000 entered against Rs 174,098
  // expected read as "reconciles".)
  const cardCrossDay = tenderRecon?.card_cross_day ?? 0
  const onlineCrossDay = tenderRecon?.online_cross_day ?? 0
  const cardExpected = cardSales + cardCrossDay
  const onlineExpected = onlineSales + onlineCrossDay
  const cardOver = cardEntered ? Math.max(0, parsedCard - cardExpected) : 0
  const cardShort = cardEntered ? Math.max(0, cardExpected - parsedCard) : 0
  const cardUnexplained = cardOver + cardShort
  const onlineOver = onlineEntered ? Math.max(0, parsedOnline - onlineExpected) : 0
  const onlineShort = onlineEntered ? Math.max(0, onlineExpected - parsedOnline) : 0
  const onlineUnexplained = onlineOver + onlineShort
  const varianceNeedsNote =
    isBigOff || cardUnexplained > varianceThreshold || onlineUnexplained > varianceThreshold
  // F-CTRL-N25: real open orders (served-but-unpaid / partially-paid tabs) still
  // holding tables & revenue must be settled/cancelled or acknowledged with a
  // note before the day locks — the backend LockClose enforces the same gate.
  const openOrdersNeedNote = unresolvedOpenOrders > 0
  const needsNote = varianceNeedsNote || openOrdersNeedNote
  const noteRequired = needsNote && notes.trim().length === 0

  // Per-tender breakdown for the review step (step 3): so the manager sees cash,
  // card and online short/over together and can explain them all in one note,
  // instead of the headline banner showing only the cash figure.
  const tenderBreakdown: TenderRow[] = [
    {
      label: 'Cash',
      entered: isCountValid,
      over: cashVariance > 0 ? cashVariance : 0,
      short: cashVariance < 0 ? -cashVariance : 0,
      needsNote: absVariance > varianceThreshold,
    },
    {
      label: 'Card',
      entered: cardEntered,
      over: cardOver,
      short: cardShort,
      needsNote: cardUnexplained > varianceThreshold,
    },
    {
      label: 'Online',
      entered: onlineEntered,
      over: onlineOver,
      short: onlineShort,
      needsNote: onlineUnexplained > varianceThreshold,
    },
  ]
  const anyTenderOff = tenderBreakdown.some((t) => t.entered && t.needsNote)

  // Headline state across EVERY tender, not just cash.
  //
  // The banner used to report `cashVariance` alone, so a day could announce
  // "Rs 15,997 short" while card was Rs 17,589 short and online Rs 2,142 over —
  // the larger problem invisible in the one line most people read. Now that all
  // three are always counted (F-CTRL-N33), the headline has to speak for all
  // three.
  //
  // Deliberately NOT a summed figure: cash, card and online are separate pots
  // (drawer, machine batch, bank), and netting an over against a short would
  // hide that two different things went wrong. When more than one is off we
  // state how many and let the breakdown below carry the numbers, rather than
  // inventing a composite total whose meaning nobody could explain.
  const offTenders = tenderBreakdown.filter((t) => t.over > 0.005 || t.short > 0.005)
  const anyTenderNeedsNote = tenderBreakdown.some((t) => t.needsNote)

  const stageMut = useMutation({
    mutationFn: () =>
      apiClient.stageCloseDay(day.id, {
        counted_cash: parsedCash,
        // All three are always sent (F-CTRL-N33). The steps above cannot be
        // advanced past without a number, so these are counted figures — an
        // explicit 0 means "this tender took nothing", never "not counted".
        counted_card: parsedCard,
        counted_online: parsedOnline,
        notes: notes.trim() || undefined,
      }),
  })

  const lockMut = useMutation({
    mutationFn: () => apiClient.lockCloseDay(day.id),
  })

  const finishing = stageMut.isPending || lockMut.isPending

  const finish = async () => {
    setLockFailed(false)
    // Always re-stage before lock — even if the day is already 'staged'
    // from a previous attempt. Reasons:
    //   1. User may have come back from review → step 1 to fix a miscount.
    //      That edited value lives only in local state until stage runs.
    //   2. User may have just typed a closing note. closing_notes is
    //      persisted by StageClose, not LockClose — without re-staging,
    //      the note never reaches the DB and the variance gate would
    //      still reject the lock.
    //   3. Fresh sales / cash movements may have landed since the prior
    //      stage. Re-staging recomputes expected_cash + cash_variance so
    //      the lock check operates on current truth, not stale numbers
    //      from the first attempt.
    // Backend StageClose accepts StateOpen | StateStaged | StateReopened
    // (dayops/service.go:931) — idempotent overwrite is the documented
    // path. The extra audit row each retry produces is accurate history.
    //
    // Two try/catches so we can tell stage-failure from lock-failure
    // without reading a possibly-stale `stageMut.isSuccess` after an
    // awaited mutation (the hook snapshot in this closure is from the
    // render that started this handler).
    try {
      await stageMut.mutateAsync()
    } catch (err) {
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      if (handleTenderCountRequired(err)) return
      toastHelpers.apiError('Save counted amounts', err as Error)
      return
    }
    try {
      await lockMut.mutateAsync()
      toastHelpers.success('Today is finished and locked')
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['dailyClosings'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
      onOpenChange(false)
    } catch (err) {
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      if (handleTenderCountRequired(err)) return
      if (handleUnreconciledOfflineItems(err)) return
      setLockFailed(true)
      toastHelpers.apiError('Finish the day', err as Error)
    }
  }

  /**
   * F-CTRL-N35: the backend refused because food fired from an offline terminal
   * was never billed. There is deliberately no note override for this one, so
   * the only way forward is to resolve the items — say exactly that, and point
   * at where the list lives, rather than leaving the manager to guess from a
   * generic failure at 2am.
   */
  const handleUnreconciledOfflineItems = (err: unknown): boolean => {
    if (!(err instanceof ApiClientError) || err.code !== 'unreconciled_offline_items') return false
    setLockFailed(true)
    qc.invalidateQueries({ queryKey: ['offline', 'unreconciled'] })
    toastHelpers.error(
      'Unbilled kitchen items',
      'Some items were sent to the kitchen from an offline terminal and never billed. ' +
        'Resolve them in the “Fired but unbilled” panel at the top of Day Close, then finish.',
    )
    return true
  }

  /**
   * F-CTRL-N33: the backend refused because a tender was never counted. Send
   * the operator to that step instead of leaving them on a Finish button that
   * keeps failing. In normal use this is unreachable — the wizard always sends
   * all three — so it exists for a stale tab running an older bundle, or a day
   * staged by a build that predates this rule.
   */
  const handleTenderCountRequired = (err: unknown): boolean => {
    if (!(err instanceof ApiClientError) || err.code !== 'tender_count_required') return false
    const missing = err.missingTenders ?? []
    setLockFailed(false)
    setStep(missing.includes('card') || missing.length === 0 ? 'count-card' : 'count-online')
    toastHelpers.error(
      'Count this first',
      'Enter the total for every payment type before finishing — type 0 if it took nothing.',
    )
    return true
  }

  // Linear navigation. Stepping back is allowed even after staging: backend
  // StageClose is idempotent on a staged day, so the manager can step back,
  // fix a miscount, and finish() re-stages with the new value. Clearing
  // lockFailed on the way back means the amber "tap Finish to lock" retry
  // banner doesn't linger across an explicit re-count.
  const goToStep = (next: Step) => {
    setLockFailed(false)
    setStep(next)
  }
  const stepIndex = STEP_ORDER.indexOf(step)
  const goNext = () => goToStep(STEP_ORDER[Math.min(stepIndex + 1, STEP_ORDER.length - 1)])
  const goBack = () => goToStep(STEP_ORDER[Math.max(stepIndex - 1, 0)])

  // Whether the current step's answer is present. Every counting step is
  // mandatory — no Skip, and Next stays dead until a number is typed.
  const canAdvance =
    step === 'count-cash'
      ? isCountValid
      : step === 'count-card'
        ? cardEntered
        : step === 'count-online'
          ? onlineEntered
          : true

  return (
    <Dialog open={open} onOpenChange={(o) => !finishing && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {step === 'count-cash' && 'Count the money in the drawer'}
            {step === 'count-card' && 'Card machine total'}
            {step === 'count-online' && 'Online & wallet total'}
            {step === 'review' && "Here's how today went"}
          </DialogTitle>
          <DialogDescription className="text-base">
            {step === 'count-cash' &&
              'Look in the till. Count every note and coin. Type the total below.'}
            {step === 'count-card' &&
              "Read today's total off the card machine. If it took nothing today, enter 0."}
            {step === 'count-online' &&
              'Total received through online and wallet payments today. If there were none, enter 0.'}
            {step === 'review' && 'Check the numbers below, then tap Finish to lock today.'}
          </DialogDescription>
          <StepIndicator step={step} />
        </DialogHeader>

        {step === 'count-cash' && (
          <TenderCountStep
            id="counted-cash"
            label="Money you counted in the drawer"
            value={countedCash}
            onChange={setCountedCash}
          />
        )}

        {step === 'count-card' && (
          <TenderCountStep
            id="counted-card"
            label="Total on the card machine"
            value={countedCard}
            onChange={setCountedCard}
            expectedLabel={
              cardCrossDay > 0
                ? `Expected on machine ${formatCurrency(cardExpected)} — ${formatCurrency(cardSales)} today + ${formatCurrency(cardCrossDay)} earlier orders`
                : `Our records show ${formatCurrency(cardSales)}`
            }
            reconciliation={
              cardEntered
                ? {
                    matches: cardUnexplained < 0.5,
                    text:
                      cardUnexplained < 0.5
                        ? '✓ Reconciles with your card records'
                        : cardOver > 0
                          ? `Over by ${formatCurrency(cardOver)} — reason needed`
                          : `Short by ${formatCurrency(cardShort)} — reason needed`,
                  }
                : undefined
            }
            crossDayNote={
              cardCrossDay > 0
                ? `${formatCurrency(cardCrossDay)} of today's card was taken for orders opened on earlier days. It lands on tonight's machine batch, but the sale is booked to those days — the expected figure above already includes it.`
                : undefined
            }
          />
        )}

        {step === 'count-online' && (
          <TenderCountStep
            id="counted-online"
            label="Total received online & by wallet"
            value={countedOnline}
            onChange={setCountedOnline}
            expectedLabel={
              onlineCrossDay > 0
                ? `Expected total ${formatCurrency(onlineExpected)} — ${formatCurrency(onlineSales)} today + ${formatCurrency(onlineCrossDay)} earlier orders`
                : `Our records show ${formatCurrency(onlineSales)}`
            }
            reconciliation={
              onlineEntered
                ? {
                    matches: onlineUnexplained < 0.5,
                    text:
                      onlineUnexplained < 0.5
                        ? '✓ Reconciles with your online/wallet records'
                        : onlineOver > 0
                          ? `Over by ${formatCurrency(onlineOver)} — reason needed`
                          : `Short by ${formatCurrency(onlineShort)} — reason needed`,
                  }
                : undefined
            }
            crossDayNote={
              onlineCrossDay > 0
                ? `${formatCurrency(onlineCrossDay)} of today's online was taken for orders opened on earlier days. It's in today's bank total, but the sale is booked to those days — the expected figure above already includes it.`
                : undefined
            }
          />
        )}

        {step === 'review' && (
          <div className="space-y-4">
            {isLoadingSummary ? (
              <SummaryBannerLoading />
            ) : (
              <SummaryBanner
                offTenders={offTenders}
                needsNote={anyTenderNeedsNote}
                formatCurrency={formatCurrency}
              />
            )}

            <button
              type="button"
              onClick={() => setShowMath((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg border bg-muted/30 px-4 py-3 text-left hover:bg-muted/50"
            >
              <span className="text-sm font-medium">Show how we got these numbers</span>
              {showMath ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            {showMath && (
              <div className="space-y-1.5 rounded-lg border bg-card px-4 py-3 text-base">
                <MathRow
                  label="Started with"
                  value={formatCurrency(day.opening_cash ?? 0)}
                />
                <MathRow label="+ Cash sales" value={formatCurrency(cashSales)} />
                {paidIn > 0 && (
                  <MathRow label="+ Money put in" value={formatCurrency(paidIn)} />
                )}
                {paidOut > 0 && (
                  <MathRow
                    label="− Money taken out"
                    value={formatCurrency(paidOut)}
                  />
                )}
                <div className="my-1 border-t" />
                <MathRow
                  label="Should be in drawer"
                  value={formatCurrency(expectedCash)}
                  bold
                />
                <MathRow
                  label="You counted"
                  value={formatCurrency(parsedCash)}
                  bold
                />
                <div className="my-1 border-t" />
                <MathRow
                  label="Difference"
                  value={`${formatCurrency(Math.abs(cashVariance))} ${
                    isMatch
                      ? ''
                      : cashVariance < 0
                        ? 'short'
                        : 'over'
                  }`.trim()}
                  bold
                  className={cn(
                    isMatch && 'text-emerald-700 dark:text-emerald-300',
                    isSmallOff && 'text-amber-700 dark:text-amber-300',
                    isBigOff && 'text-rose-700 dark:text-rose-300',
                  )}
                />

                {/* Card and online get the same expected-vs-counted arithmetic
                    as cash. Before F-CTRL-N33 this panel was cash-only, so a
                    manager who wanted to check a card figure had no way to see
                    where the expected total came from. */}
                <div className="my-2 border-t" />
                <MathRow label="Card machine expected" value={formatCurrency(cardExpected)} />
                <MathRow label="You counted" value={formatCurrency(parsedCard)} />
                <MathRow
                  label="Difference"
                  value={tenderDeltaLabel(parsedCard - cardExpected, formatCurrency)}
                  className={varianceToneClass(parsedCard - cardExpected, varianceThreshold)}
                />
                <div className="my-2 border-t" />
                <MathRow label="Online & wallet expected" value={formatCurrency(onlineExpected)} />
                <MathRow label="You counted" value={formatCurrency(parsedOnline)} />
                <MathRow
                  label="Difference"
                  value={tenderDeltaLabel(parsedOnline - onlineExpected, formatCurrency)}
                  className={varianceToneClass(parsedOnline - onlineExpected, varianceThreshold)}
                />
              </div>
            )}

            {/* Always rendered (F-CTRL-N33). Every tender is counted by the
                time we reach this step, and the review must read as a
                three-tender statement — the old gate hid card and online
                entirely whenever the step had been skipped, which is exactly
                how a card shortfall stayed invisible. */}
            <TenderBreakdown
              rows={tenderBreakdown}
              anyOff={anyTenderOff}
              formatCurrency={formatCurrency}
            />
            {hasCrossDay && (
              <p className="text-xs text-muted-foreground">
                Card/online expected totals include{' '}
                {[
                  cardCrossDay > 0 ? `${formatCurrency(cardCrossDay)} card` : null,
                  onlineCrossDay > 0 ? `${formatCurrency(onlineCrossDay)} online` : null,
                ]
                  .filter(Boolean)
                  .join(' + ')}{' '}
                taken today for orders opened on earlier days — on today's batch, but booked to
                those days.
              </p>
            )}

            {openOrdersNeedNote && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="text-amber-900 dark:text-amber-200">
                  <span className="font-semibold">
                    {unresolvedOpenOrders} open order{unresolvedOpenOrders === 1 ? '' : 's'} still unresolved
                  </span>{' '}
                  — settle or cancel them (Open Orders on the counter), or add a note below explaining
                  why they're left open, before the day can lock.
                </div>
              </div>
            )}

            {showNotes || needsNote ? (
              <div className="space-y-2">
                <Label htmlFor="closing-notes" className="text-base">
                  Note for the owner
                  {needsNote ? (
                    <span className="ml-1 text-sm font-normal text-rose-700 dark:text-rose-300">
                      {varianceNeedsNote && openOrdersNeedNote
                        ? '(required — explain the difference and the open orders)'
                        : openOrdersNeedNote
                          ? '(required — acknowledge the open orders)'
                          : '(required — please explain the difference)'}
                    </span>
                  ) : (
                    <span className="ml-1 text-sm font-normal text-muted-foreground">
                      (optional)
                    </span>
                  )}
                </Label>
                <Textarea
                  id="closing-notes"
                  rows={2}
                  placeholder={
                    needsNote
                      ? 'e.g. drawer short; card total higher (old bill paid); Table 5 still dining'
                      : 'Anything the owner should know about today'
                  }
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={cn(
                    noteRequired && 'border-rose-400 focus-visible:ring-rose-400',
                  )}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowNotes(true)}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" /> Add a note for the owner (optional)
              </button>
            )}

            {lockFailed && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600 shrink-0" />
                <div>
                  <div className="font-semibold text-amber-900 dark:text-amber-200">
                    Almost done — tap Finish to lock
                  </div>
                  <div className="text-sm text-amber-800 dark:text-amber-300">
                    Your counted amounts were saved. The last step didn't finish, so tap
                    Finish the day again.
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Once you tap Finish, today's numbers are saved and only the owner can change them.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {/* No Skip on any counting step (F-CTRL-N33) — every tender is
              counted, and "it took nothing" is entered as a typed 0. */}
          {step !== 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={step === 'count-cash' ? () => onOpenChange(false) : goBack}
              >
                {step === 'count-cash' ? 'Cancel' : 'Back'}
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 sm:flex-none"
                onClick={goNext}
                disabled={!canAdvance}
              >
                Next <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={goBack}
                disabled={finishing}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={finish}
                disabled={finishing || noteRequired || isLoadingSummary}
              >
                {finishing ? (
                  'Saving…'
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    {lockFailed ? 'Finish the day' : 'Finish the day'}
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function StepIndicator({ step }: { step: Step }) {
  const stepNumber = STEP_ORDER.indexOf(step) + 1
  const total = STEP_ORDER.length
  return (
    <div className="flex items-center gap-1.5 pt-2" aria-label={`Step ${stepNumber} of ${total}`}>
      {STEP_ORDER.map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-2 rounded-full transition-all',
            i + 1 === stepNumber ? 'w-8 bg-foreground' : 'w-2 bg-muted',
            i + 1 < stepNumber && 'bg-emerald-500',
          )}
          aria-hidden="true"
        />
      ))}
      <span className="ml-2 text-sm text-muted-foreground">
        Step {stepNumber} of {total}
      </span>
    </div>
  )
}

/** "Rs 120 short" / "Rs 40 over" / "Rs 0" — direction stated, never a bare signed number. */
function tenderDeltaLabel(delta: number, formatCurrency: (n: number) => string): string {
  if (Math.abs(delta) < 0.01) return formatCurrency(0)
  return `${formatCurrency(Math.abs(delta))} ${delta < 0 ? 'short' : 'over'}`
}

/** Green when it reconciles, amber inside the threshold, rose past it. */
function varianceToneClass(delta: number, threshold: number): string {
  const abs = Math.abs(delta)
  if (abs < 0.01) return 'text-emerald-700 dark:text-emerald-300'
  if (abs <= threshold) return 'text-amber-700 dark:text-amber-300'
  return 'text-rose-700 dark:text-rose-300'
}

/**
 * Placeholder rendered in the review step while `currentDayStatusQuery` or
 * `movementsQuery` is still resolving. We can't show a meaningful "drawer
 * matches" / "Rs X short" banner until those queries land — without them
 * `cashSales` defaults to 0 and the variance computation degenerates to
 * `counted_cash − opening_cash`, which falsely reads as "matches" for the
 * common Rs-1 opening-float-only case. Skeleton bar matches the SummaryBanner
 * height so the dialog doesn't jump when the real banner takes over.
 */
function SummaryBannerLoading() {
  return (
    <div
      className="flex items-center gap-3 rounded-xl border-2 border-muted bg-muted/30 p-4 animate-pulse"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="h-9 w-9 rounded-full bg-muted shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-5 w-40 rounded bg-muted" />
        <div className="h-3.5 w-64 rounded bg-muted/70" />
      </div>
    </div>
  )
}

/**
 * The one line most people actually read. It must describe the whole day, not
 * one tender.
 *
 * It used to render `cashVariance` alone, so a close could announce
 * "Rs 15,997 short" while card was Rs 17,589 short and online Rs 2,142 over —
 * the bigger hole invisible in the headline and only findable by reading the
 * breakdown underneath. Now that every tender is counted (F-CTRL-N33), all
 * three speak here.
 *
 * When several are off it reports the COUNT, not a sum. Cash, card and online
 * are separate pots — a drawer, a machine batch, a bank account — so netting
 * one's surplus against another's shortfall would state a number that is true
 * of nothing and would hide that two independent things went wrong.
 */
function SummaryBanner({
  offTenders,
  needsNote,
  formatCurrency,
}: {
  offTenders: TenderRow[]
  needsNote: boolean
  formatCurrency: (n: number) => string
}) {
  if (offTenders.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/40">
        <div className="rounded-full bg-emerald-500 p-1.5 shrink-0">
          <Check className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="text-lg font-bold text-emerald-900 dark:text-emerald-100">
            Everything matches!
          </div>
          <div className="text-sm text-emerald-800 dark:text-emerald-200">
            Cash, card and online all match what we expected. Great job.
          </div>
        </div>
      </div>
    )
  }

  const tone = needsNote
    ? {
        border: 'border-rose-300 dark:border-rose-800',
        bg: 'bg-rose-50 dark:bg-rose-950/40',
        chip: 'bg-rose-500',
        title: 'text-rose-900 dark:text-rose-100',
        body: 'text-rose-800 dark:text-rose-200',
      }
    : {
        border: 'border-amber-300 dark:border-amber-800',
        bg: 'bg-amber-50 dark:bg-amber-950/40',
        chip: 'bg-amber-500',
        title: 'text-amber-900 dark:text-amber-100',
        body: 'text-amber-800 dark:text-amber-200',
      }

  let headline: string
  let detail: string
  if (offTenders.length === 1) {
    const t = offTenders[0]
    const amount = t.over > 0 ? t.over : t.short
    headline = `${t.label} ${formatCurrency(amount)} ${t.over > 0 ? 'over' : 'short'}`
    detail = needsNote
      ? "That's a big difference. Please write a note below so the owner knows what happened."
      : "That's a small difference. You can add a note below if you know why."
  } else {
    headline = `${offTenders.length} payment types don't match`
    detail = needsNote
      ? `${offTenders.map((t) => t.label).join(', ')} — check each one below and explain them in the note.`
      : `${offTenders.map((t) => t.label).join(', ')} are slightly off. Check them below.`
  }

  return (
    <div className={cn('flex items-start gap-3 rounded-xl border-2 p-4', tone.border, tone.bg)}>
      <div className={cn('rounded-full p-1.5 shrink-0', tone.chip)}>
        <AlertTriangle className="h-5 w-5 text-white" />
      </div>
      <div>
        <div className={cn('text-lg font-bold', tone.title)}>{headline}</div>
        <div className={cn('text-sm', tone.body)}>{detail}</div>
      </div>
    </div>
  )
}

function MathRow({
  label,
  value,
  bold,
  className,
}: {
  label: string
  value: string
  bold?: boolean
  className?: string
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={cn(bold && 'font-semibold')}>{label}</span>
      <span className={cn('tabular-nums', bold ? 'font-bold' : 'font-medium', className)}>
        {value}
      </span>
    </div>
  )
}

/** One payment type's short/over on the review step. */
interface TenderRow {
  label: string
  entered: boolean
  over: number
  short: number
  /** True once the short/over crosses the note threshold. */
  needsNote: boolean
}

/**
 * Step-3 per-payment-type breakdown. The headline banner only speaks to cash;
 * this lists cash / card / online short/over side by side so the manager can
 * account for every difference in a single note instead of guessing which
 * tender the required note is about.
 */
function TenderBreakdown({
  rows,
  anyOff,
  formatCurrency,
}: {
  rows: TenderRow[]
  anyOff: boolean
  formatCurrency: (n: number) => string
}) {
  const shown = rows.filter((r) => r.entered)
  if (shown.length === 0) return null
  return (
    <div className="space-y-1.5 rounded-lg border bg-card px-4 py-3">
      <div className="text-sm font-semibold text-muted-foreground">
        {anyOff ? 'Differences to explain' : 'By payment type'}
      </div>
      {shown.map((r) => {
        const isOver = r.over > 0.5
        const isShort = r.short > 0.5
        const matches = !isOver && !isShort
        return (
          <div key={r.label} className="flex items-center justify-between text-base">
            <span>{r.label}</span>
            <span
              className={cn(
                'tabular-nums font-medium',
                matches && 'text-emerald-600 dark:text-emerald-300',
                !matches && r.needsNote && 'text-rose-600 dark:text-rose-300',
                !matches && !r.needsNote && 'text-amber-600 dark:text-amber-300',
              )}
            >
              {matches
                ? '✓ Matches'
                : isOver
                  ? `${formatCurrency(r.over)} over`
                  : `${formatCurrency(r.short)} short`}
            </span>
          </div>
        )
      })}
    </div>
  )
}
